import * as THREE from 'three';
import type { CameraSettings } from './types';

/**
 * Owns the game's camera. The game uses one deliberately fixed tactical view:
 * a top-down camera that never follows the player or changes perspective.
 */
export class CameraController {
  public readonly camera: THREE.PerspectiveCamera;

  private initialized = false;
  private readonly topPosition = new THREE.Vector3();
  private readonly topTarget = new THREE.Vector3();

  public constructor(camera: THREE.PerspectiveCamera = new THREE.PerspectiveCamera()) {
    this.camera = camera;
  }

  /** Establish the fixed top-down pose immediately. */
  public initialize(settings: CameraSettings): void {
    this.applyFov(settings.fov);
    this.applyTopDownPose(settings);
    this.initialized = true;
  }

  /** Immediately restore the fixed top-down pose after a reset or scene setup. */
  public snap(settings: CameraSettings): void {
    this.initialize(settings);
  }

  /**
   * Refresh the top-down camera settings. `dt` remains part of the update
   * contract so the render loop does not need camera-specific branching.
   */
  public update(_dt: number, settings: CameraSettings): void {
    this.applyFov(settings.fov);
    if (!this.initialized) {
      this.initialize(settings);
      return;
    }
    this.applyTopDownPose(settings);
  }

  public getMode(): 'top' {
    return 'top';
  }

  private readonly applyFov = (fov: number): void => {
    if (this.camera.fov === fov) return;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  };

  private readonly applyTopDownPose = (settings: CameraSettings): void => {
    const cameraHeight = Math.tan(THREE.MathUtils.degToRad(settings.tilt)) * settings.distance;
    this.topPosition.set(0, cameraHeight, settings.distance);
    this.topTarget.set(0, 0, settings.lookAhead);
    this.camera.position.copy(this.topPosition);
    this.camera.lookAt(this.topTarget);
  };
}
